import { NativeEventSource, EventSourcePolyfill } from "event-source-polyfill";
import { useEffect, useRef, useState } from "react";
import asyncRetry from "./asyncRetry";
let instance: SSEHandler;
const EventSource = NativeEventSource || EventSourcePolyfill;

const EVENT_ACCESS_URL = `${window._env_.PLATFORM_API_DOMAIN}/v1/events/access`;
const EVENT_STREAM_URL = `${window._env_.PLATFORM_API_DOMAIN}/v1/events`;
const LOCAL_STORAGE_KEY = "platfi-ui-app/SESSION";

export enum EventsAvailable {
  membershipUpgraded = "entity/membership/upgraded",
  membershipUpdated = "entity/membership/updated",
  snapshotGeneration = "entity/ledger/snapshot/generation",
  snapshotTimeline = "entity/ledger/snapshot/timeline",
  profileUpdate = "entity/ledger/profile/update",
  casePaid = "payment/completed",
  matchResultUpdate = "funding/match/status_update",
  relatedCompaniesUpdate = "entity/ledger/snapshot/related/synced",
}

interface Data extends Record<string, unknown> {
  entityId: string;
  companyNumber: string;
  finished: boolean;
  provider: "experian";
  percentage: number;
  withFacilities?: boolean;
  message?: string;
  error?: boolean;
}

export interface EventStreamPayloadData {
  message: string;
  data: Data;
  event: EventsAvailable;
  metadata?: {
    createdAt?: string;
  };
}

interface EventStreamPayload {
  id?: string;
  type: string | "message";
  target: EventSourcePolyfill;
  data: string; //EventStreamPayloadData
}

interface AccessTokenResponse {
  id: string;
  expiresIn: number;
  expiresAt: string;
  createdAt: string;
  scope: "SSE_ACCESS_SCOPE";
  value: string;
  generatedBy: string;
  generatedByEntityId: number;
}

export interface Store {
  id: string;
  onMessage: (event: EventStreamPayload) => void;
  onError: (eventError: unknown) => void;
}

const getUserSession = (): Promise<string> =>
  new Promise((resolve, reject) => {
    if (localStorage.getItem("anonymous-user") === "true") {
      reject("Anonymous user error");
    }

    try {
      const userSession = localStorage.getItem(LOCAL_STORAGE_KEY);
      const { accessToken } = JSON.parse(userSession);
      resolve(accessToken);
    } catch (error) {
      reject(error);
    }
  });

const requestAccessTokenDefault = (): Promise<AccessTokenResponse> => {
  return new Promise((resolve, reject) => {
    try {
      asyncRetry(getUserSession, 700).then((accessToken) => {
        resolve(
          fetch(EVENT_ACCESS_URL, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }).then((response) => response.json())
        );
      });
    } catch (error) {
      reject((error as Error).message);
    }
  });
};

type EventSourceType = typeof EventSourcePolyfill;

class SSEHandler {
  started: boolean;
  token: string;
  private eventSource: typeof EventSource;
  stores: Store[];

  constructor() {
    if (instance) {
      throw new Error("SSEHandler instance already created");
    }
    this.stores = [];
    this.started = false;
    instance = this;
  }

  private onMessage(event: EventStreamPayload) {
    instance.stores.forEach((store) => store.onMessage(event));
  }

  private onError(eventError: unknown) {
    instance.stores.forEach((store) => store.onError(eventError));
  }

  private onOpen() {}

  private openConnection(token: string) {
    const eventSourceInstance = new EventSource(
      `${EVENT_STREAM_URL}?token=${token}`
    );
    eventSourceInstance.onmessage = this.onMessage;
    eventSourceInstance.onerror = this.onError;
    eventSourceInstance.onopen = this.onOpen;
    this.eventSource = eventSourceInstance as unknown as EventSourceType;
  }

  private appendStore(store: Store) {
    const storeIndex = this.stores.findIndex(
      (persistStore) => persistStore.id === store.id
    );
    if (storeIndex === -1) {
      this.stores.push(store);
    } else {
      this.stores[storeIndex] = store;
    }
  }

  init(
    store?: Store,
    requestAccessToken: () => Promise<AccessTokenResponse> = requestAccessTokenDefault
  ) {
    if (store) {
      this.appendStore(store);
    }
    if (this.eventSource || this.started) {
      return;
    }
    this.started = true;
    requestAccessToken()
      .then((result) => this.openConnection(result.value))
      .catch((error) => console.warn(error));
  }

  closeStore(storeKey: string) {
    this.stores = this.stores.filter((store) => store.id !== storeKey);
  }
}

const SSEHandlerInstance = new SSEHandler();
export default SSEHandlerInstance;

export const adapterReduxStore = (store: { dispatch: Function }): Store => ({
  id: "reduxStore",
  onMessage: (event) => {
    const objEvent = JSON.parse(event.data);
    store.dispatch({
      type: `@@SSE/${objEvent.event}`,
      payload: objEvent.data,
    });
  },
  onError: () => {},
});

export const SSEReduxMiddleware =
  ({ dispatch }: { dispatch: Function; getState: Function }) =>
  (next: Function) =>
  (action: unknown) => {
    SSEHandlerInstance.init({
      id: "redux",
      onMessage: (event) => {
        const objEvent = JSON.parse(event.data);

        const sendSSEEvent = () => {
          return {
            type: `@@SSE/${objEvent.event}`,
            payload: objEvent.data,
          };
        };

        dispatch(sendSSEEvent());
      },
      onError: () => {},
    });
    next(action);
  };

export const useSSEEvents = (
  id?: string,
  applyFilter?: (eventData: EventStreamPayloadData) => boolean
) => {
  const [eventData, setEventData] =
    useState<EventStreamPayloadData | undefined>();
  const curentId = useRef(
    id || Math.floor(Math.random() * Date.now()).toString(16)
  );

  const onMessage = (event: EventStreamPayload) => {
    try {
      const eventData =
        event?.data && (JSON.parse(event?.data) as EventStreamPayloadData);
      const shouldDispatchEvent = applyFilter ? applyFilter(eventData) : true;
      if (shouldDispatchEvent) {
        setEventData(eventData);
      }
    } catch (e) {}
  };

  const onError = (error: unknown) => {
    setEventData(error.data);
  };

  useEffect(() => {
    SSEHandlerInstance.init({
      id: curentId.current,
      onMessage,
      onError,
    });
    return () => {
      SSEHandlerInstance.closeStore(curentId.current);
    };
  }, []);

  return eventData;
};
